import { useCallback, useRef } from 'react';
import { tabNavEqualWidthClass, tabNavRootClass } from './tabClasses.js';

function getTabButtons(container) {
  if (!container) return [];
  return Array.from(container.querySelectorAll('[role="tab"]:not([disabled])'));
}

function focusTabAt(container, index) {
  const tabs = getTabButtons(container);
  if (!tabs.length) return;
  const nextIndex = Math.max(0, Math.min(index, tabs.length - 1));
  tabs[nextIndex]?.focus();
}

/**
 * Horizontal tab list container — Figma Governance slide-out (node 280:11044).
 *
 * Wraps {@link Tab} children with `role="tablist"` and arrow-key navigation.
 *
 * @example
 * <TabNav ariaLabel="Section tabs" equalWidth>
 *   <Tab selected onClick={() => setTab('one')}>Tab 1</Tab>
 *   <Tab onClick={() => setTab('two')}>Tab 2</Tab>
 * </TabNav>
 *
 * @param {object} props
 * @param {import('react').ReactNode} props.children
 * @param {string} [props.className]
 * @param {string} [props.ariaLabel]
 * @param {boolean} [props.equalWidth=false] - Stretch tabs evenly (Governance layout).
 */
const TabNav = ({ children, className = '', ariaLabel, equalWidth = false }) => {
  const listRef = useRef(null);

  // [] is safe: only closes over listRef (stable) and DOM APIs — no props or state.
  const handleKeyDown = useCallback((event) => {
    const container = listRef.current;
    if (!container) return;

    const tabs = getTabButtons(container);
    if (!tabs.length) return;

    const currentIndex = tabs.indexOf(document.activeElement);
    if (currentIndex === -1) return;

    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    focusTabAt(container, nextIndex);
  }, []);

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      className={[
        tabNavRootClass,
        equalWidth ? tabNavEqualWidthClass : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  );
};

export default TabNav;
